import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format an ISO date string as date-only (no time), in user's locale
export function formatDateOnly(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  } as Intl.DateTimeFormatOptions)
}

// Utility to generate consistent colors from tag names
export function getTagColor(tagName: string): string {
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ]

  let hash = 0
  for (let i = 0; i < tagName.length; i++) {
    hash = tagName.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

// Basic title case: capitalize first letter, keep rest lowercase
export function toTitleCase(text: string): string {
  if (!text) return ''
  return text
    .split(/\s+|_/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

// Format an ISO date string into user's local timezone with a friendly format
export function formatDateWithTimezone(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  } as Intl.DateTimeFormatOptions)
}

// Lightweight background color helper for Todo cards
// Depends only on a subset of TodoResponse fields used in the UI
type TodoLike = {
  completed: boolean
  due_date?: string
}

export function getCardBackgroundColor(todo: TodoLike): string {
  if (todo.completed) return 'bg-gray-50'
  if (todo.due_date) {
    const now = new Date()
    const due = new Date(todo.due_date)
    const diffMs = due.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return 'bg-red-50'
    if (diffDays <= 2) return 'bg-yellow-50'
  }
  return 'bg-white'
}
